// Editor state — single object holding everything the editor reads and
// writes per session. The trackData inside is schema-shaped and
// already-validated (caller passes it in after loadTrack).
//
// The editor is single-instance, so module-level helpers (selection
// helpers, dim/hex utilities) can take state as a parameter and stay
// pure. Mutation lives only in the renderer's onAfterMount and the
// per-mode tools that we'll wire up in phase 4b+.

import { newEmptyTrack } from '../../shared/track-schema.js';

export function createEditorState(track) {
  return {
    trackData: track ?? newEmptyTrack(),
    mode:      'track',                               // 'track' | 'walls' | 'surfaces' | 'markers' | 'pitlane' | 'racingline' | 'labels'
    camera:    { x: 0, y: 0, zoom: 4.5 },
    selectedIndices: [],
    activeWallIdx:   -1,
    activePatchIdx:  -1,
    activeMarker:    null,
    activePitMarker: null,
    activeLabelIdx:  -1,
    // Labels mode placement sub-state. null = selection mode (clicks
    // pick existing labels). Otherwise a partial label being placed:
    //   { kind: 'corner' }                                  → next centerline click drops a corner
    //   { kind: 'area' }                                    → next centerline click sets the area's start
    //   { kind: 'area', startSegIdx, startT, startX, startY } → next click sets the area's end and commits
    pendingLabel:    null,
    hover:     null,
    drag:      null,
    cursor:    { x: 0, y: 0, inCanvas: false },
    snapTarget: null,
    history:   { stack: [], cursor: -1 },
    _trackEdges: null,                                // cached per render
    calibrationMode:   null,
    calibrationPoint1: null,
  };
}

// ---------- selection ----------
export const isSelected      = (state, i) => state.selectedIndices.includes(i);
export const setSelection    = (state, arr) => { state.selectedIndices = [...new Set(arr)].sort((a, b) => a - b); };
export const clearSelection  = (state) => { state.selectedIndices = []; };
export const toggleInSelection = (state, i) => {
  if (isSelected(state, i)) state.selectedIndices = state.selectedIndices.filter(x => x !== i);
  else setSelection(state, [...state.selectedIndices, i]);
};

// ---------- "active geometry" ----------
// The waypoint array currently being edited:
//   track mode      → trackline
//   walls mode      → the active wall
//   surfaces mode   → the active patch's polygon
//   pitlane mode    → the pit lane spline (always open)
//   markers         → trackline (markers position on the racing line)
//   racingline mode → the authored racing line (closed loop)

export function activeWaypoints(state) {
  if (state.mode === 'walls' && state.activeWallIdx >= 0) {
    return state.trackData.walls[state.activeWallIdx]?.waypoints ?? [];
  }
  if (state.mode === 'surfaces' && state.activePatchIdx >= 0) {
    return state.trackData.surfacePatches[state.activePatchIdx]?.polygon ?? [];
  }
  if (state.mode === 'pitlane' && state.trackData.pitLane) {
    return state.trackData.pitLane.waypoints;
  }
  if (state.mode === 'racingline' && state.trackData.racingLine) {
    return state.trackData.racingLine.waypoints;
  }
  return state.trackData.trackline.waypoints;
}

export function activeAlpha(state) {
  if (state.mode === 'walls' && state.activeWallIdx >= 0) {
    return state.trackData.walls[state.activeWallIdx]?.alpha ?? 0.5;
  }
  if (state.mode === 'surfaces' && state.activePatchIdx >= 0) {
    return state.trackData.surfacePatches[state.activePatchIdx]?.alpha ?? 0.5;
  }
  if (state.mode === 'pitlane' && state.trackData.pitLane) {
    return state.trackData.pitLane.alpha ?? 0.5;
  }
  if (state.mode === 'racingline' && state.trackData.racingLine) {
    return state.trackData.racingLine.alpha ?? 0.5;
  }
  return state.trackData.trackline.alpha ?? 0.5;
}

export function activeIsClosed(state) {
  if (state.mode === 'walls' && state.activeWallIdx >= 0) {
    return !!state.trackData.walls[state.activeWallIdx]?.closed;
  }
  if (state.mode === 'surfaces' && state.activePatchIdx >= 0) {
    const p = state.trackData.surfacePatches[state.activePatchIdx];
    return p?.closed !== false;
  }
  if (state.mode === 'pitlane') return false;
  // racingline is always a closed loop — same shape as the trackline.
  return true;
}

export function hasActiveEntity(state) {
  if (state.mode === 'track' || state.mode === 'markers') return true;
  if (state.mode === 'walls') return state.activeWallIdx >= 0;
  if (state.mode === 'surfaces') return state.activePatchIdx >= 0;
  if (state.mode === 'pitlane') return !!state.trackData.pitLane;
  if (state.mode === 'racingline') return !!state.trackData.racingLine;
  return false;
}

export const trackAlpha = (state) => state.trackData.trackline.alpha ?? 0.5;

// ---------- color helpers ----------
// Multiply the alpha of an rgba(...) string by a factor — used to dim
// inactive entities while preserving hue.
export function dimColor(rgba, factor) {
  return rgba.replace(/[\d.]+\)$/, m => (parseFloat(m) * factor).toFixed(3) + ')');
}

// #rrggbb → rgba(...) with the given alpha. Curb rendering uses this
// because CURB_COLORS stores hex (so swatches can use hex as a solid
// background) but the inactive curb state needs translucency.
export function hexWithAlpha(hex, alpha) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!m) return hex;
  const r = parseInt(m[1], 16), g = parseInt(m[2], 16), b = parseInt(m[3], 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
